import type { IconName } from '@televet/kibble-ui/Icon';
import {
  PROMPT_TEMPLATE_ICONS,
  PROMPT_TEMPLATE_ICON_COLORS,
  type PromptTemplateIcon,
  type PromptTemplateIconColor,
} from '@astro/shared-utilities/prompt-template-presentation';

export {
  PROMPT_TEMPLATE_ICONS,
  PROMPT_TEMPLATE_ICON_COLORS,
  type PromptTemplateIcon,
  type PromptTemplateIconColor,
};

// Exhaustive Records over the allow-lists, so adding a value without a Kibble equivalent fails
// the build rather than rendering an undefined icon at runtime.
export const promptTemplateIconMap: Record<PromptTemplateIcon, IconName> = {
  DollarCircle: 'dollarCircle',
  Paw: 'paw',
  Report: 'report',
  CalendarCheck: 'calendarCheck',
  Invoice: 'invoice',
  TaskList: 'taskList',
  CheckList: 'checkList',
  Clock: 'clock',
  Team: 'team',
  Health: 'health',
  Target: 'targetSolid',
  LightBulb: 'lightBulb',
};

export interface PromptTemplateIconTint {
  backgroundColor: string;
  color: string;
  // The colour word rather than the token name. An admin picking a tint for a transaction-charge
  // card should not have to choose "Danger" to get red.
  label: string;
}

// Token pairs lifted from Kibble's Tag recipe, so the tints track the theme in dark mode.
export const promptTemplateIconTintMap: Record<PromptTemplateIconColor, PromptTemplateIconTint> = {
  Default: {
    backgroundColor: 'background.subtle.less',
    color: 'text.default',
    label: 'Neutral',
  },
  Info: {
    backgroundColor: 'background.info',
    color: 'text.info',
    label: 'Blue',
  },
  Success: {
    backgroundColor: 'background.success',
    color: 'text.success',
    label: 'Green',
  },
  Warning: {
    backgroundColor: 'background.warning.contrast',
    color: 'text.warning',
    label: 'Amber',
  },
  Danger: {
    backgroundColor: 'background.danger',
    color: 'text.danger',
    label: 'Red',
  },
};

export const DEFAULT_PROMPT_TEMPLATE_ICON: IconName = 'report';
export const DEFAULT_PROMPT_TEMPLATE_TINT: PromptTemplateIconTint =
  promptTemplateIconTintMap.Default;

export const isPromptTemplateIcon = (value: string): value is PromptTemplateIcon =>
  (PROMPT_TEMPLATE_ICONS as readonly string[]).includes(value);

export const isPromptTemplateIconColor = (value: string): value is PromptTemplateIconColor =>
  (PROMPT_TEMPLATE_ICON_COLORS as readonly string[]).includes(value);

// Takes a plain string because the column is a string. A value written outside the API, by a
// manual query or a value retired since the row was saved, falls back instead of throwing.
export function resolvePromptTemplateIcon(icon: string | null | undefined): IconName {
  if (!icon || !isPromptTemplateIcon(icon)) {
    return DEFAULT_PROMPT_TEMPLATE_ICON;
  }
  return promptTemplateIconMap[icon];
}

export function resolvePromptTemplateTint(
  iconColor: string | null | undefined
): PromptTemplateIconTint {
  if (!iconColor || !isPromptTemplateIconColor(iconColor)) {
    return DEFAULT_PROMPT_TEMPLATE_TINT;
  }
  return promptTemplateIconTintMap[iconColor];
}
